import { Field, FieldAttributes, useField, useFormikContext } from 'formik';
import React, { memo, useEffect, useRef } from 'react';

import { isCurrentErrorFirst } from 'Utilities/newyu';
import { BLANK_ERROR } from 'Utilities/yup';

type Props = FieldAttributes<{
  hideMessage?: boolean;
  noTouch?: boolean;
  innerRef?: React.MutableRefObject<HTMLElement | null>;
}>;

/**
 * This component expects to be placed inside of Formik so that you can apply invalid formatting
 * and display the error message below the input. You are expected to pass in all necessary props
 * for the resulting Formik Field to render correctly.
 *
 * Required Props:
 *
 * - name - the name of the field. This name must correspond to the Formik value
 *
 * Optional Props:
 *
 * - className - will be applied to the Formik Field that is drawing your input
 * - hideMessage - setting this to true will prevent drawing the error message to the DOM
 * - noTouch - if you want the error to display for the field even if it wasn't marked as touched
 * - everything else - will be passed to the Formik Field that is drawing your input
 */
const ErrorField: React.FC<React.PropsWithChildren<Props>> = ({
  hideMessage,
  noTouch,
  innerRef: parentRef,
  ...props
}) => {
  const { className, name, ...other } = props;
  const [field, meta] = useField(props);
  const { errors, status, submitCount } = useFormikContext();

  const errored = (noTouch || meta.touched) && meta.error;
  const errorId = (other.id || name) + '-error-text';

  // Manages the checked property for you since formik does not set it explicitly
  if (other.type === 'checkbox' && !other.hasOwnProperty('checked')) {
    field.checked = !!field.value === true;
  }

  const fieldRef = useRef<HTMLElement>(null);
  const previousSubmitCount = useRef(0);

  useEffect(() => {
    if (errored && fieldRef.current && status) {
      if (previousSubmitCount.current !== submitCount) {
        if (isCurrentErrorFirst(errors, name, status)) {
          fieldRef.current.scrollIntoView({
            block: 'center',
            inline: 'center',
            behavior: 'smooth',
          });
        }

        previousSubmitCount.current = submitCount;
      }
    }
  }, [errored, errors, name, status, submitCount]);

  return (
    <>
      <Field
        className={`${className} ${errored ? 'is-invalid' : ''}`}
        innerRef={fieldRef}
        {...field}
        {...other}
      />
      {errored && meta.error !== BLANK_ERROR && !hideMessage ? (
        <div id={errorId} className="invalid-feedback text-md">
          {meta.error}
        </div>
      ) : null}
    </>
  );
};

export default memo(ErrorField);
